export interface WorkbookFilter {
  displayName: string;
  apiField: string;
  type: string;
  displayField: string | null;
  required: boolean;
  cascadeFromApiField: string | null;
  dropdownEndpoint: string;
  fixedValue?: string;
}

export interface WorkbookColumn {
  apiField: string;
  displayName: string;
  excelFormat: string;
}

export interface WorkbookStateResult {
  templateId: string;
  templateName: string;
  entityType: string;
  entityDataEndpoint: string;
  filters: WorkbookFilter[];
  columns: WorkbookColumn[];
  udfs: string;
  hasUdfs: boolean;
}

const TEMPLATE_ID_PROPERTY_NAME = "BuilderMetrix.TemplateId";

async function readWorkbookTemplateId(context: Excel.RequestContext): Promise<string> {
  try {
    context.workbook.properties.load("custom");
    await context.sync();
    const prop = context.workbook.properties.custom.getItemOrNullObject(TEMPLATE_ID_PROPERTY_NAME);
    prop.load("value");
    await context.sync();
    return prop.isNullObject ? "" : String(prop.value ?? "").trim();
  } catch (err) {
    console.log("Could not retrieve BuilderMetrix.TemplateId property:", err);
    return "";
  }
}

function parseFixedFilterValues(dataCallStr: string): Map<string, string> {
  const fixedValues = new Map<string, string>();
  const conditionRegex = /(\w+)\s+eq\s+('(?:[^']|'')*'|[^\]\[]+)/g;
  let match: RegExpExecArray | null;

  while ((match = conditionRegex.exec(dataCallStr)) !== null) {
    const apiField = match[1];
    const rhsRaw = (match[2] || "").trim();
    let parsedValue = rhsRaw;

    if (rhsRaw.startsWith("'") && rhsRaw.endsWith("'")) {
      parsedValue = rhsRaw.slice(1, -1).replace(/''/g, "'");
    }

    if (parsedValue.startsWith("%(") && parsedValue.endsWith(")")) {
      continue;
    }

    fixedValues.set(apiField, parsedValue);
  }

  return fixedValues;
}

function normalizeConditionToken(value: string): string {
  return (value || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function parseFilterTypeString(typeStr: string): { type: string; apiField: string | null; displayField: string | null } {
  const trimmedType = (typeStr || "").trim();
  const normalizedType = trimmedType.toLowerCase();
  const match = trimmedType.match(/^(select|multiselect)\s*\(\s*([^,]+?)\s*,\s*([^)]+?)\s*\)\s*[#*]?$/i);
  if (match) {
    return { type: match[1].toLowerCase(), apiField: match[2].trim(), displayField: match[3].trim() };
  }
  if (normalizedType === "date" || normalizedType === "number" || normalizedType === "text") {
    return { type: normalizedType, apiField: null, displayField: null };
  }
  return { type: normalizedType, apiField: null, displayField: null };
}

function parseDropdownCall(callStr: string, currentApiField: string | null): { endpoint: string; cascadeFromApiField: string | null } {
  if (!callStr) return { endpoint: "", cascadeFromApiField: null };
  const ampIdx = callStr.indexOf("&FilterValue=");
  if (ampIdx === -1) return { endpoint: callStr, cascadeFromApiField: null };
  const endpoint = callStr.substring(0, ampIdx);
  const condition = callStr.substring(ampIdx + "&FilterValue=".length);
  const normalizedCurrentField = normalizeConditionToken(currentApiField || "");

  const conditionRegex = /(\w+)\s+(?:eq|in)\s+/g;
  const parsedFields: string[] = [];
  let match: RegExpExecArray | null;
  while ((match = conditionRegex.exec(condition)) !== null) {
    parsedFields.push(match[1]);
  }

  const cascadeFromApiField =
    parsedFields.find((field) => normalizeConditionToken(field) !== normalizedCurrentField) || null;

  return { endpoint, cascadeFromApiField };
}

function parseRequiredApiFields(dataCallStr: string): Set<string> {
  const required = new Set<string>();
  const beforeBrackets = dataCallStr.split("[")[0];
  const filterValueIdx = beforeBrackets.indexOf("&FilterValue=");
  if (filterValueIdx === -1) return required;
  const conditions = beforeBrackets.substring(filterValueIdx + "&FilterValue=".length);
  const parts = conditions.split(" and ");
  for (const part of parts) {
    const fieldMatch = part.trim().match(/^(\w+)\s+(?:eq|in)\s+/);
    if (fieldMatch) required.add(fieldMatch[1]);
  }
  return required;
}

function extractEntityTypeFromDataCall(dataCallStr: string): string {
  return dataCallStr.split(/[&?]/)[0].trim();
}

function isTableHeaderRow(row: string[]): boolean {
  const first = String(row[0] ?? "").trim().toLowerCase();
  const second = String(row[1] ?? "").trim().toLowerCase();
  const third = String(row[2] ?? "").trim().toLowerCase();

  return (
    first === "parameters" ||
    first === "field" ||
    (second === "type" && third === "call")
  );
}

function findFirstDataRowIndex(configValues: string[][], startIndex: number): number {
  for (let i = startIndex; i < configValues.length; i++) {
    if (String(configValues[i][1] ?? "").trim() === "(data)") {
      return i;
    }
  }
  return -1;
}

export async function readWorkbookState(): Promise<WorkbookStateResult | null> {
  try {
    return await Excel.run(async (context) => {
      const configSheet = context.workbook.worksheets.getItemOrNullObject("(configuration)");
      await context.sync();

      if (configSheet.isNullObject) return null;

      const configRange = configSheet.getUsedRange();
      configRange.load("values");
      await context.sync();

      const configValues = configRange.values as string[][];
      if (!configValues || configValues.length < 2) return null;

      const hasTableHeader = isTableHeaderRow(configValues[0]);
      const dataRowIndex = findFirstDataRowIndex(configValues, 1);
      if (dataRowIndex === -1) return null;

      const dataRow = configValues[dataRowIndex];

      const templateName = String(dataRow[3] ?? "").trim() || String(configValues[0][0] ?? "").trim() || String(dataRow[0] ?? "").trim();
      const dataCallStr = String(dataRow[2] ?? "");
      const entityType = extractEntityTypeFromDataCall(dataCallStr) || String(dataRow[0] ?? "");
      const entityDataEndpoint = hasTableHeader ? entityType : String(configValues[0][2] ?? "");

      const requiredApiFields = parseRequiredApiFields(dataCallStr);
      const fixedFilterValuesByApiField = parseFixedFilterValues(dataCallStr);
      const fixedFilterValuesByNormalizedToken = new Map<string, string>();
      fixedFilterValuesByApiField.forEach((fixedValue, apiField) => {
        const normalized = normalizeConditionToken(apiField);
        if (normalized && !fixedFilterValuesByNormalizedToken.has(normalized)) {
          fixedFilterValuesByNormalizedToken.set(normalized, fixedValue);
        }
      });

      const filters: WorkbookFilter[] = [];
      for (let i = 1; i < dataRowIndex; i++) {
        const row = configValues[i];
        const displayName = String(row[0] ?? "");
        const typeStr = String(row[1] ?? "");
        const callStr = String(row[2] ?? "");
        if (!displayName || !typeStr) continue;

        const { type, apiField: parsedApiField, displayField } = parseFilterTypeString(typeStr);
        const { endpoint: dropdownEndpoint, cascadeFromApiField } = parseDropdownCall(callStr, parsedApiField ?? displayName);
        const apiField = parsedApiField ?? displayName;
        const required = requiredApiFields.has(apiField);
        const fixedValue =
          fixedFilterValuesByApiField.get(apiField) ??
          fixedFilterValuesByNormalizedToken.get(normalizeConditionToken(apiField)) ??
          fixedFilterValuesByNormalizedToken.get(normalizeConditionToken(displayName)) ??
          "";

        filters.push({ displayName, apiField, type, displayField, required, cascadeFromApiField, dropdownEndpoint, fixedValue });
      }

      const filterByNormalizedApiField = new Map<string, WorkbookFilter>();
      filters.forEach((filter) => {
        const normalizedApiField = normalizeConditionToken(filter.apiField);
        if (normalizedApiField && !filterByNormalizedApiField.has(normalizedApiField)) {
          filterByNormalizedApiField.set(normalizedApiField, filter);
        }
      });

      const queue: WorkbookFilter[] = filters.filter((filter) => filter.required);
      const visited = new Set<string>();

      while (queue.length > 0) {
        const current = queue.shift() as WorkbookFilter;
        const cascadeFrom = normalizeConditionToken(current.cascadeFromApiField || "");
        if (!cascadeFrom || visited.has(cascadeFrom)) continue;

        const parent = filterByNormalizedApiField.get(cascadeFrom);
        if (!parent) continue;

        visited.add(cascadeFrom);
        if (!parent.required) {
          parent.required = true;
          queue.push(parent);
        }
      }

      const colsSheet = context.workbook.worksheets.getItemOrNullObject("(columns)");
      await context.sync();

      const columns: WorkbookColumn[] = [];
      if (!colsSheet.isNullObject) {
        const colsRange = colsSheet.getUsedRange();
        colsRange.load("values");
        await context.sync();

        const colsValues = colsRange.values as string[][];
        for (let i = 1; i < colsValues.length; i++) {
          const apiField = String(colsValues[i][0] ?? "");
          const excelFormat = String(colsValues[i][1] ?? "");
          const displayName = String(colsValues[i][2] ?? "");
          if (apiField) columns.push({ apiField, displayName, excelFormat });
        }
      }

      let udfs = "";
      let hasUdfs = false;
      const paramsSheet = context.workbook.worksheets.getItemOrNullObject("(parameters)");
      await context.sync();

      if (!paramsSheet.isNullObject) {
        const paramsRange = paramsSheet.getUsedRange();
        paramsRange.load("values");
        await context.sync();

        const paramsValues = paramsRange.values as string[][];
        if (paramsValues && paramsValues.length > 0) {
          for (const row of paramsValues) {
            const marker = String(row[0] ?? "").trim().toLowerCase();
            const udfValue = String(row[1] ?? "").trim();
            if (marker === "udfs" && udfValue) {
              udfs = udfValue;
              hasUdfs = true;
              break;
            }
          }
        }
      }

      if (!entityType) return null;

      const templateId = await readWorkbookTemplateId(context);

      return { templateId, templateName, entityType, entityDataEndpoint, filters, columns, udfs, hasUdfs };
    });
  } catch (err) {
    console.error("readWorkbookState error:", err);
    return null;
  }
}
